import React, { useId } from 'react';

interface ConbersaLogoProps {
	className?: string;
	size?: number;
}

export const ConbersaLogo: React.FC<ConbersaLogoProps> = ({
	className = "",
	size = 24
}) => {
	// Generate unique IDs for gradients to avoid conflicts when multiple logos are on the same page
	const uniqueId = useId();
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 200 200"
			xmlns="http://www.w3.org/2000/svg"
			className={className}
		>
			<defs>
				{/* Primary gradient for main faces */}
				<linearGradient id={`primaryGradient-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="100%">
					<stop offset="0%" style={{ stopColor: "#c2705a", stopOpacity: 1 }} />
					<stop offset="50%" style={{ stopColor: "#a0583f", stopOpacity: 1 }} />
					<stop offset="100%" style={{ stopColor: "#8b4934", stopOpacity: 1 }} />
				</linearGradient>

				{/* Darker gradient for shadow faces */}
				<linearGradient id={`shadowGradient-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="100%">
					<stop offset="0%" style={{ stopColor: "#8b4934", stopOpacity: 1 }} />
					<stop offset="50%" style={{ stopColor: "#7a3d2b", stopOpacity: 1 }} />
					<stop offset="100%" style={{ stopColor: "#6b3222", stopOpacity: 1 }} />
				</linearGradient>

				{/* Light gradient for highlight faces */}
				<linearGradient id={`lightGradient-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="100%">
					<stop offset="0%" style={{ stopColor: "#e0b8a0", stopOpacity: 1 }} />
					<stop offset="50%" style={{ stopColor: "#c2705a", stopOpacity: 1 }} />
					<stop offset="100%" style={{ stopColor: "#a0583f", stopOpacity: 1 }} />
				</linearGradient>

				{/* Accent gradient for small elements */}
				<linearGradient id={`accentGradient-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="100%">
					<stop offset="0%" style={{ stopColor: "#f0ddd0", stopOpacity: 1 }} />
					<stop offset="100%" style={{ stopColor: "#e0b8a0", stopOpacity: 1 }} />
				</linearGradient>
			</defs>

			{/* Main geometric "C" shape */}
			{/* Outer left face */}
			<path d="M 30 60 L 30 140 L 50 160 L 50 80 Z" fill={`url(#shadowGradient-${uniqueId})`} />

			{/* Top horizontal face */}
			<path d="M 30 60 L 130 60 L 150 40 L 50 40 Z" fill={`url(#lightGradient-${uniqueId})`} />

			{/* Top right vertical face */}
			<path d="M 130 60 L 130 80 L 150 60 L 150 40 Z" fill={`url(#primaryGradient-${uniqueId})`} />

			{/* Inner top horizontal */}
			<path d="M 50 80 L 110 80 L 130 60 L 50 60 Z" fill={`url(#primaryGradient-${uniqueId})`} />

			{/* Inner vertical connector */}
			<path d="M 110 80 L 110 120 L 130 100 L 130 60 Z" fill={`url(#shadowGradient-${uniqueId})`} />

			{/* Inner bottom horizontal */}
			<path d="M 50 120 L 110 120 L 130 140 L 50 140 Z" fill={`url(#primaryGradient-${uniqueId})`} />

			{/* Bottom horizontal face */}
			<path d="M 30 140 L 130 140 L 150 160 L 50 160 Z" fill={`url(#shadowGradient-${uniqueId})`} />

			{/* Bottom right vertical face */}
			<path d="M 130 140 L 130 120 L 150 140 L 150 160 Z" fill={`url(#lightGradient-${uniqueId})`} />

			{/* Small geometric accent elements (conversation bubbles abstracted) */}
			<circle cx="160" cy="70" r="8" fill={`url(#accentGradient-${uniqueId})`} />
			<circle cx="175" cy="85" r="6" fill={`url(#lightGradient-${uniqueId})`} />
			<circle cx="165" cy="100" r="4" fill={`url(#primaryGradient-${uniqueId})`} />

			{/* Bottom right accent cluster */}
			<rect x="155" y="130" width="12" height="12" rx="2" fill={`url(#accentGradient-${uniqueId})`} />
			<rect x="170" y="140" width="8" height="8" rx="1" fill={`url(#lightGradient-${uniqueId})`} />
		</svg>
	);
};

export default ConbersaLogo;
